import styled, { css } from 'styled-components';
import { colors, spacing, breakpoints } from '../../constants/theme';

export const Card = styled.div<{ 
  variant?: 'default' | 'elevated' | 'outlined' | 'feature' | 'result';
  $hoverable?: boolean;
  $clickable?: boolean;
}>`
  background: ${props => {
    switch (props.variant) {
      case 'elevated':
        return colors.background.secondary;
      case 'outlined':
        return 'transparent';
      case 'feature':
        return colors.background.paper;
      default:
        return colors.background.tertiary;
    }
  }};
  
  border: 1px solid ${colors.background.tertiary};
  border-radius: ${props => props.variant === 'feature' ? '16px' : '12px'};
  padding: ${props => props.variant === 'feature' ? spacing.xl : spacing.lg};
  position: relative;
  transition: all 0.3s ease;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  
  ${props => props.variant === 'elevated' && css`
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    backdrop-filter: blur(10px);
  `}

  ${props => props.variant === 'feature' && css`
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    
    @media (max-width: ${breakpoints.tablet}) {
      padding: ${spacing.lg};
    }

    @media (max-width: ${breakpoints.mobile}) {
      padding: ${spacing.md};
      border-radius: 12px;
    }
  `}

  ${props => props.variant === 'result' && css`
    border-color: ${colors.accent?.main || colors.primary.main}40;
    background: linear-gradient(135deg, ${colors.background.tertiary} 0%, ${colors.accent?.main || colors.primary.main}10 100%);
    
    &:hover {
      border-color: ${colors.accent?.main || colors.primary.main};
      box-shadow: 0 8px 32px ${colors.accent?.main || colors.primary.main}30;
    }
  `}

  ${props => (props.$hoverable || props.$clickable) && css`
    &:hover {
      transform: translateY(-4px);
      border-color: ${colors.primary.main};
      box-shadow: ${props.variant === 'feature' 
        ? '0 15px 35px rgba(99, 102, 241, 0.2)'
        : `0 8px 32px ${colors.primary.main}20`
      };

      @media (max-width: ${breakpoints.tablet}) {
        transform: translateY(-2px);
      }
    }
  `}
`;

export const CardHeader = styled.div<{ withDivider?: boolean }>`
  margin-bottom: ${spacing.lg};
  
  ${props => props.withDivider && css`
    padding-bottom: ${spacing.lg};
    border-bottom: 1px solid ${colors.background.tertiary};
  `}
`;

export const CardContent = styled.div`
  flex: 1;
`;

export const CardFooter = styled.div<{ withDivider?: boolean }>`
  margin-top: ${spacing.lg};
  
  ${props => props.withDivider && css`
    padding-top: ${spacing.lg};
    border-top: 1px solid ${colors.background.tertiary};
  `}
`;

export const CardIcon = styled.div<{ size?: 'sm' | 'md' | 'lg' }>`
  ${props => {
    const size = props.size || 'md';
    switch (size) {
      case 'sm':
        return css`
          width: 32px;
          height: 32px;
          font-size: 16px;
        `;
      case 'lg':
        return css`
          width: 64px;
          height: 64px;
          font-size: 32px;
        `;
      default:
        return css`
          width: 48px;
          height: 48px;
          font-size: 24px;
        `;
    }
  }}
  
  background: ${colors.primary.main}20;
  color: ${colors.primary.main};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${spacing.md};
  border: 2px solid ${colors.primary.main}30;
`;

export const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 ${spacing.sm} 0;
  line-height: 1.4;
`;

export const CardDescription = styled.p`
  font-size: 14px;
  color: ${colors.text.secondary};
  margin: 0;
  line-height: 1.5;
  flex: 1;
`;
